import React, { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import moment from "moment";
import HistoryPaymentComponent from "../components";
import { PER_PAGE_SIZE, statusReservation } from "../../../../utils/constants";
import {
  getFilteredTime,
  downloadBlob,
  getCurrentUtc,
} from "../../../../utils/utils";
import {
  getHistoryPayment,
  postSavePayment,
  exportHistoryPayment,
  exportTopSpender,
  getDetailPayment,
  updatePayment,
} from "../../../../services/Handlers/PaymentService";
import {
  postUseActivePackage,
  deleteActivePackage,
} from "../../../../services/Handlers/PackageService";
import { getCategoryItems } from "../../../../services/Handlers/MasterCategoryItemService";
import { useNotification } from "../../../../context/NotificationProvider";
import { useLocalization } from "../../../../context/LocalizeContextProvider";
import { useHome } from "../../../../context/HomeProvider";
import { useAuth } from "../../../../context/AuthProvider";
import { defaultDetailReservation } from "../../Reservation/container";
import { getDetailReservation } from "../../../../services/Handlers/ReservationService";
import { useModal } from "../../../../context/ModalProvider";
import { roleUser } from "../../../../utils/constants";

const defaultFilter = {
  type: { label: "Harian", value: "day" },
  from: moment().startOf("month").format("YYYY-MM-DD"),
  to: moment().endOf("month").format("YYYY-MM-DD"),
  page: 0,
  size: PER_PAGE_SIZE,
  category_item_id: null,
  doctor_id: null,
  method: null,
  paymentType: null,
  busines_id: null,
  current_utc: null,
  created_by_id: { label: "Semua Staff", value: null },
};

export default function HistoryPayment() {
  const currentUtc = getCurrentUtc();
  const notification = useNotification();
  const localize = useLocalization();
  const modal = useModal();
  const { dataDoctors, isLoadingDoctor } = useHome();
  const { userDetail, role } = useAuth();
  const isDoctor =
    role === roleUser.ROLE_DOCTOR || role === roleUser.ROLE_BEAUTICIAN;
  const isOwner = role === roleUser.ROLE_OWNER;

  const [totalPage, setTotalPage] = useState(0);
  const [totalEntries, setTotalEntries] = useState(0);
  const [dataHistoryPayment, setDataHistoryPayment] = useState([]);
  const [filterHistory, setFilterHistory] = useState(
    JSON.parse(JSON.stringify(defaultFilter))
  );
  const [dataCategoryItem, setDataCategoryItem] = useState([]);

  const [dataDetailReservation, setDataDetailReservation] = useState(
    defaultDetailReservation
  );
  const [dataDetailPayment, setDataDetailPayment] = useState(null);
  const [showModalDetail, setShowModalDetail] = useState(null);
  const [showModalAddPayment, setShowModalAddPayment] = useState(false);

  const [showModalDetailPayment, setShowModalDetailPayment] = useState(null);

  const {
    isLoading: isLoadingDetailPayment,
    isFetching: isFetchingDetailPayment,
  } = useQuery(
    ["DetailPayment", showModalDetailPayment],
    () => getDetailPayment(showModalDetailPayment),
    {
      onSuccess: (res) => {
        if (res.status === 200 || res.status === 201) {
          const response = res.data;
          if (response) {
            setDataDetailPayment(response);
          }
        } else {
          notification.error(localize.getText("unknownMessageError"));
        }
      },
      enabled: !!showModalDetailPayment,
    }
  );

  const { isLoading, mutate: mutateHistory } = useMutation(
    ["GetHistoryPayment"],
    (data) => getHistoryPayment(data),
    {
      onSuccess: (res) => {
        if (res.status === 200 || res.status === 201) {
          if (res?.data?.data.length > 0) {
            // Prefer doctor_name from RPC response; fall back to client-side
            // join against dataDoctors if the upstream payload omits it
            // (defensive — staging proxy and prod Lambda response shapes
            // may differ during the migration).
            const merged = res.data.data.map((item) =>
              item.doctor_name
                ? item
                : {
                    ...item,
                    doctor_name:
                      dataDoctors.find((d) => d.id === item.doctor_id)?.name ||
                      "",
                  }
            );
            setDataHistoryPayment(merged);
          } else {
            setDataHistoryPayment([]);
          }
          setTotalEntries(res?.data?.total);
          setTotalPage(Math.ceil(res?.data?.total / filterHistory.size));
        } else {
          notification.error(
            res?.data?.detail || localize.getText("unknownMessageError")
          );
        }
      },
    }
  );

  const { isFetching: isLoadingCategory } = useQuery(
    ["CategoryItemList", userDetail?.businessId],
    () =>
      getCategoryItems({
        businessId: userDetail?.businessId,
        page: 0,
        size: 99999,
      }),
    {
      onSuccess: (res) => {
        if (res.status === 200 || res.status === 201) {
          setDataCategoryItem(res?.data.data || []);
        }
      },
      enabled: !!userDetail?.businessId,
    }
  );

  const { isLoading: isLoadingSubmitPayment, mutate: mutateSubmitPayment } =
    useMutation(postSavePayment, {
      onSuccess: (res) => {
        if (res.status === 200 || res.status === 201) {
          mutateHistory({
            ...filterHistory,
            current_utc: currentUtc,
            busines_id: userDetail.businessId,
            created_by_id: isOwner ? null : userDetail?.id,
          });
          notification.success(localize.getText("successAddPayment"));
          handleClearAllFormModal();
        } else {
          notification.error(
            res?.data?.detail || localize.getText("unknownMessageError")
          );
        }
      },
      onError: () => {
        notification.error(localize.getText("unknownMessageError"));
      },
    });

  const { isLoading: isLoadingUpdatePayment, mutate: mutateUpdatePayment } =
    useMutation(updatePayment, {
      onSuccess: (res) => {
        if (res.status === 200 || res.status === 201) {
          mutateHistory({
            ...filterHistory,
            current_utc: currentUtc,
            busines_id: userDetail.businessId,
            created_by_id: isOwner ? null : userDetail?.id,
          });
          notification.success(localize.getText("successUpdatePayment"));
          handleClearAllFormModal();
        } else {
          notification.error(
            res?.data?.detail || localize.getText("unknownMessageError")
          );
        }
      },
      onError: () => {
        notification.error(localize.getText("unknownMessageError"));
      },
    });

  const { isLoading: isLoadingDetail, isFetching: isFetchingDetail } = useQuery(
    ["DetailReservation", showModalDetail],
    () => getDetailReservation(showModalDetail),
    {
      onSuccess: (res) => {
        if (res.status === 200 || res.status === 201) {
          const response = res.data;
          if (response) {
            const status = statusReservation.find(
              (item) => item.value === response.status
            );
            const reservationTimeFormat = getFilteredTime(
              response.duration_visit,
              response.time_slots
            )[0];
            const reservationTime =
              moment(
                reservationTimeFormat.availability_start,
                "HH:mm:ss"
              ).format("HH:mm") +
              " - " +
              moment(reservationTimeFormat.availability_end, "HH:mm:ss").format(
                "HH:mm"
              );

            setDataDetailReservation((prevState) => ({
              ...prevState,
              id: response.id,
              patientId: response.patient_id,
              reservationDate: moment(
                response.reservation_date,
                "YYYY-MM-DD"
              ).format("dddd DD MMMM YYYY"),
              reservationTime,
              durationVisit: response.duration_visit,
              doctorName:
                dataDoctors.find((value) => value.id === response.doctor_id)
                  ?.name || "",
              doctorId: response.doctor_id,
              status,
              noSequence: response.no_sequence,
              identityNumber: response.user_identity_number,
              identityType: response.user_identity_type,
              name: response.user_name,
              email: response.user_email,
              phoneNumber: response.user_phone_number,
              dateOfBirth: moment(
                response.user_date_of_birth,
                "YYYY-MM-DD"
              ).format("DD MMMM YYYY"),
              gender: response.user_gender,
              payment: response.payment,
              payment_parent: response.payment_parent,
              user_id: response.user_id,
              service_id: response?.service_id,
              is_patient_new: response?.is_patient_new || false,
              actived_reservations: response?.actived_reservations || [],
              actived_packages: response?.actived_packages || [],
              actived_deposits: response?.actived_deposits || [],
              wallet_balance: response?.wallet_balance || 0,
            }));
          }
        } else {
          notification.error(localize.getText("unknownMessageError"));
        }
      },
      enabled: !!showModalDetail,
    }
  );

  const { isLoading: isLoadingExport, mutate: mutateExportHistory } =
    useMutation(
      ["ExportHistoryPayment"],
      (data) => exportHistoryPayment(data),
      {
        onSuccess: (res, variables) => {
          if (res.status === 200 || res.status === 201) {
            let filename = "Laporan-Riwayat-Pembayaran-Lengkap";
            if (variables?.format === "netto") {
              filename = "Laporan-Riwayat-Pembayaran-Netto";
            } else if (variables?.is_marketing) {
              filename = "Laporan-Riwayat-Pembayaran-Marketing";
            }
            const dateStr = moment().format("DD-MM-YYYY_HH-mm-ss");
            downloadBlob(res.data, `${filename}_${dateStr}`);
          } else {
            notification.error(
              res?.data?.detail || localize.getText("unknownMessageError")
            );
          }
        },
      }
    );

  const { isLoading: isLoadingUsePackage, mutate: mutateUsePackage } =
    useMutation(postUseActivePackage, {
      onSuccess: (res) => {
        if (res.status === 200 || res.status === 201) {
          notification.success(localize.getText("successUsePackage"));
          handleClearAllFormModal();
        } else {
          notification.error(
            res?.data?.detail || localize.getText("unknownMessageError")
          );
        }
      },
      onError: () => {
        notification.error(localize.getText("unknownMessageError"));
      },
    });

  const { isLoading: isLoadingDeletePackage, mutate: mutateDeletePackage } =
    useMutation(deleteActivePackage, {
      onSuccess: (res) => {
        if (res.status === 200 || res.status === 201) {
          notification.success(localize.getText("successDeletePackage"));
          handleClearAllFormModal();
        } else {
          notification.error(
            res?.data?.detail || localize.getText("unknownMessageError")
          );
        }
      },
      onError: () => {
        notification.error(localize.getText("unknownMessageError"));
      },
    });

  useEffect(() => {
    if (userDetail?.businessId) {
      if (isDoctor) {
        handleChangeFilter("doctor_id", {
          value: userDetail.id,
          label: userDetail.firstName,
        });
      }

      mutateHistory({
        ...filterHistory,
        doctor_id: isDoctor ? userDetail.id : null,
        busines_id: userDetail.businessId,
        current_utc: currentUtc,
        created_by_id: isOwner ? null : userDetail?.id,
      });
    }
    // eslint-disable-next-line
  }, [userDetail]);

  const handleChangeFilter = async (name, value) => {
    if (name === "type") {
      await setFilterHistory((prevState) => ({
        ...prevState,
        from: null,
        to: null,
      }));
    } else if (
      name === "from" &&
      filterHistory.to &&
      moment(value, "YYYY-MM-DD").isAfter(
        moment(filterHistory.to, "YYYY-MM-DD")
      )
    ) {
      await setFilterHistory((prevState) => ({
        ...prevState,
        to: null,
      }));
    }
    setFilterHistory((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleClickFilter = () => {
    handleChangeFilter("page", 0);
    mutateHistory({
      ...filterHistory,
      page: 0,
      method: filterHistory?.method?.value,
      category_item_id: filterHistory?.category_item_id?.value,
      doctor_id: filterHistory?.doctor_id?.value,
      paymentType: filterHistory?.paymentType?.value,
      busines_id: userDetail.businessId,
      current_utc: currentUtc,
      created_by_id: isOwner ? null : userDetail?.id,
    });
  };

  const handleChangeSizePerPage = (size) => {
    handleChangeFilter("size", size);
    handleChangeFilter("page", 0);
    mutateHistory({
      ...filterHistory,
      size,
      page: 0,
      method: filterHistory?.method?.value,
      category_item_id: filterHistory?.category_item_id?.value,
      doctor_id: filterHistory?.doctor_id?.value,
      paymentType: filterHistory?.paymentType?.value,
      busines_id: userDetail.businessId,
      current_utc: currentUtc,
      created_by_id: isOwner ? null : userDetail?.id,
    });
  };

  const handlePagination = (value) => {
    handleChangeFilter("page", value);
    mutateHistory({
      ...filterHistory,
      page: value,
      method: filterHistory?.method?.value,
      category_item_id: filterHistory?.category_item_id?.value,
      doctor_id: filterHistory?.doctor_id?.value,
      paymentType: filterHistory?.paymentType?.value,
      busines_id: userDetail?.businessId,
      current_utc: currentUtc,
      created_by_id: isOwner ? null : userDetail?.id,
    });
  };

  const handleClearFilter = async () => {
    let selectedDoctor = null;
    if (isDoctor) {
      selectedDoctor = {
        value: userDetail.id,
        label: userDetail.firstName,
      };
    }

    await setFilterHistory({
      ...defaultFilter,
      doctor_id: selectedDoctor,
      current_utc: currentUtc,
      type: filterHistory.type,
      from: null,
      to: null,
      category_item_id: null,
      method: null,
      paymentType: null,
      created_by_id: { label: "Semua Staff", value: null },
    });
    mutateHistory(defaultFilter);
  };

  const handleHideShowModalDetial = (value) => {
    if (!value) {
      setDataDetailReservation(defaultDetailReservation);
    }
    setShowModalDetail(value);
  };

  const handleHideShowModalDetialPayment = (value) => {
    if (!value) {
      setDataDetailPayment(null);
    }
    setShowModalDetailPayment(value);
  };

  const handleClearAllFormModal = () => {
    setDataDetailReservation(defaultDetailReservation);
    setDataDetailPayment(null);
    setShowModalDetail(null);
    setShowModalAddPayment(false);
    setShowModalDetailPayment(null);
    modal.dismiss();
  };

  const handleClickPayment = (value) => {
    const data = {
      bookingId: dataDetailReservation.id,
      businessId: userDetail.businessId,
      updatedBy: userDetail?.firstName,
      createdById: isOwner ? null : userDetail?.id,
      ...value,
    };

    mutateSubmitPayment(data);
  };

  const handleClickUpdatePayment = (value) => {
    const data = {
      businessId: userDetail.businessId,
      updatedBy: userDetail?.firstName,
      paymentId: showModalDetailPayment,
      ...value,
    };

    mutateUpdatePayment(data);
  };

  const handleClickPaymentPharmacy = (value) => {
    const data = {
      bookingId: null,
      businessId: userDetail.businessId,
      createdBy: userDetail?.firstName,
      updatedBy: userDetail?.firstName,
      createdById: isOwner ? null : userDetail?.id,
      ...value,
    };

    mutateSubmitPayment(data);
  };

  const handleClickExport = () => {
    mutateExportHistory({
      ...filterHistory,
      busines_id: userDetail.businessId,
      method: filterHistory?.method?.value,
      category_item_id: filterHistory?.category_item_id?.value,
      doctor_id: filterHistory?.doctor_id?.value,
      paymentType: filterHistory?.paymentType?.value,
      current_utc: currentUtc,
      created_by_id: isOwner ? null : userDetail?.id,
    });
  };

  const handleClickExportNetto = () => {
    mutateExportHistory({
      ...filterHistory,
      busines_id: userDetail.businessId,
      method: filterHistory?.method?.value,
      category_item_id: filterHistory?.category_item_id?.value,
      doctor_id: filterHistory?.doctor_id?.value,
      paymentType: filterHistory?.paymentType?.value,
      current_utc: currentUtc,
      created_by_id: isOwner ? null : userDetail?.id,
      format: "netto",
    });
  };

  const handleClickExportMarketing = () => {
    mutateExportHistory({
      ...filterHistory,
      busines_id: userDetail.businessId,
      method: filterHistory?.method?.value,
      category_item_id: filterHistory?.category_item_id?.value,
      doctor_id: filterHistory?.doctor_id?.value,
      paymentType: filterHistory?.paymentType?.value,
      current_utc: currentUtc,
      is_marketing: true,
      created_by_id: isOwner ? null : userDetail?.id,
    });
  };

  const { isLoading: isLoadingExportTopSpender, mutate: mutateExportTopSpender } =
    useMutation(
      ["ExportTopSpender"],
      (data) => exportTopSpender(data),
      {
        onSuccess: (res) => {
          if (res.status === 200 || res.status === 201) {
            const filename = "Laporan-Riwayat-Pembayaran-Top-Spender";
            const dateStr = moment().format("DD-MM-YYYY_HH-mm-ss");
            downloadBlob(res.data, `${filename}_${dateStr}`);
          } else {
            notification.error(
              res?.data?.detail || localize.getText("unknownMessageError")
            );
          }
        },
      }
    );

  const handleClickExportTopSpender = () => {
    mutateExportTopSpender({
      from: filterHistory?.from,
      to: filterHistory?.to,
      busines_id: userDetail.businessId,
      current_utc: currentUtc,
    });
  };

  const handleUsePackage = (id) => {
    const dataPackage = {
      patientId: dataDetailReservation.patientId,
      bookingId: dataDetailReservation.id,
      packageId: id,
      paymentId: dataDetailReservation?.payment?.id || null,
      businessId: userDetail.businessId,
      createdBy: userDetail?.firstName,
      updatedBy: userDetail?.firstName,
      createdById: isOwner ? null : userDetail?.id,
    };

    mutateUsePackage(dataPackage);
  };

  const handleDeletePackage = (id) => {
    mutateDeletePackage(id);
  };

  return (
    <HistoryPaymentComponent
      dataHistoryPayment={dataHistoryPayment}
      isLoading={isLoading}
      totalPage={totalPage}
      totalEntries={totalEntries}
      activePage={filterHistory?.page}
      handlePagination={handlePagination}
      filterHistory={filterHistory}
      handleChangeFilter={handleChangeFilter}
      handleClickFilter={handleClickFilter}
      handleClearFilter={handleClearFilter}
      handleChangeSizePerPage={handleChangeSizePerPage}
      dataCategoryItem={dataCategoryItem}
      dataDoctors={dataDoctors}
      isLoadingDoctor={isLoadingDoctor}
      isLoadingCategory={isLoadingCategory}
      isLoadingExport={isLoadingExport || isLoadingExportTopSpender}
      handleHideShowModalDetial={handleHideShowModalDetial}
      handleClickExport={handleClickExport}
      dataDetailReservation={dataDetailReservation}
      isLoadingModalDetail={
        isLoadingDetail ||
        isFetchingDetail ||
        isLoadingSubmitPayment ||
        isLoadingUsePackage ||
        isLoadingDeletePackage
      }
      businessId={userDetail?.businessId}
      showModalDetail={showModalDetail}
      handleClickPayment={handleClickPayment}
      handleClickPaymentPharmacy={handleClickPaymentPharmacy}
      showModalAddPayment={showModalAddPayment}
      setShowModalAddPayment={setShowModalAddPayment}
      isLoadingSubmitPayment={isLoadingSubmitPayment}
      isDoctor={isDoctor}
      isOwner={isOwner}
      handleHideShowModalDetialPayment={handleHideShowModalDetialPayment}
      dataDetailPayment={dataDetailPayment}
      isLoadingDetailPayment={
        isLoadingDetailPayment ||
        isFetchingDetailPayment ||
        isLoadingUpdatePayment
      }
      showModalDetailPayment={showModalDetailPayment}
      handleClickUpdatePayment={handleClickUpdatePayment}
      handleUsePackage={handleUsePackage}
      handleDeletePackage={handleDeletePackage}
      handleClickExportMarketing={handleClickExportMarketing}
      handleClickExportNetto={handleClickExportNetto}
      handleClickExportTopSpender={handleClickExportTopSpender}
    />
  );
}
